import { useEffect, useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { getProfile, logout, updateProfile } from "~/api/auth"
import type { ProfileResponse } from "~/api/auth"
import { HeroPortraitCard } from "~/components/HeroPortraitCard"
import { LoggedInScreenCard } from "~/components/LoggedInScreenCard"
import { PageShell } from "~/components/PageShell"
import { ProfileFeedbackSection } from "~/components/ProfileFeedbackSection"
import { SearchableBreedSelector } from "~/components/SearchableBreedSelector"
import { usePortraitCelebrationRedirect } from "~/hooks/usePortraitCelebrationRedirect"
import { assetUrl } from "~/utils/assetUrl"
import { heroPortraitUrl } from "~/utils/heroPortraitUrl"
import { ChevronDown, ChevronRight, PawPrint, Star } from "lucide-react"
import { hasEntitlement, ENTITLEMENTS } from "~/utils/entitlements"

type DogSex = "male" | "female"

export function ProfilePage() {
  const navigate = useNavigate()
  const [profile, setProfile] = useState<ProfileResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [dogName, setDogName] = useState("")
  const [dogSex, setDogSex] = useState<DogSex | null>(null)
  const [dogBreed, setDogBreed] = useState("")
  const [dogMixDetail, setDogMixDetail] = useState("")
  const [breedValid, setBreedValid] = useState(true)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    getProfile()
      .then((data) => {
        setProfile(data)
        const dog = data.dog_profile
        if (dog) {
          setDogName(dog.name)
          setDogSex(dog.sex)
          setDogBreed(dog.breed ?? "")
          setDogMixDetail(dog.mix_detail ?? "")
        }
        if (data.user.needs_free_dog_choice) {
          navigate("/choose-free-dog", { replace: true })
        }
      })
      .catch(() => navigate("/login", { replace: true }))
      .finally(() => setLoading(false))
  }, [navigate])

  async function handleSaveDogProfile() {
    if (!profile?.dog_profile || !dogSex || !breedValid) return
    setSaveError(null)
    setSaving(true)
    try {
      const updated = await updateProfile({
        dog_name: dogName.trim(),
        sex: dogSex,
        breed: dogBreed || null,
        mix_detail: dogBreed ? dogMixDetail || null : null,
      })
      setProfile(updated)
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : "Could not save")
    } finally {
      setSaving(false)
    }
  }

  async function handleLogout() {
    await logout()
    navigate("/login", { replace: true })
  }

  function handleOpenDogDetails(dogId: number) {
    navigate(`/pack/dogs/${dogId}`)
  }

  usePortraitCelebrationRedirect(profile, navigate, !loading && Boolean(profile))

  if (loading) {
    return (
      <PageShell>
        <p className="text-[var(--color-text-secondary)]">Loading...</p>
      </PageShell>
    )
  }

  if (!profile) return null

  const dog = profile.dog_profile
  const displayName = dog ? dogName.trim() || dog.name : dogName.trim() || "Your dog"
  const archetypeName = dog?.archetype_label ?? "Archetype"
  const archetypeIcon = dog?.archetype_icon
  const canSaveDog =
    Boolean(dog) &&
    breedValid &&
    dogName.trim().length > 0 &&
    dogSex !== null &&
    (dogName.trim() !== dog.name ||
      dogSex !== dog.sex ||
      (dogBreed || "") !== (dog.breed || "") ||
      (dogMixDetail || "") !== (dog.mix_detail || ""))
  const isPlusUser = hasEntitlement(profile.user.entitlements, ENTITLEMENTS.multipleDogs)
  const myDogs = isPlusUser
    ? (profile.dogs ?? []).filter((d) => !d.plus_locked)
    : dog
      ? [ { ...dog, id: dog.id ?? profile.active_dog_profile_id ?? 0 } ]
      : []

  return (
    <PageShell innerClassName="max-w-lg w-full" fixedTabNav>
      <LoggedInScreenCard>
        <div className="flex justify-center">
          <button
            type="button"
            className="inline-flex items-center gap-2 rounded-full border border-[var(--color-divider)] bg-[var(--color-bg-primary)] px-4 py-2 text-sm font-medium text-[var(--color-text-primary)]"
          >
            <PawPrint size={14} />
            {displayName}
            <ChevronDown size={14} />
          </button>
        </div>

        {dog && (
          <div className="mt-4 flex flex-col items-center">
            {archetypeIcon ? (
              <img
                src={assetUrl(`/images/archetypes/logos/${archetypeIcon}`)}
                alt={archetypeName}
                className="h-16 w-16 object-contain"
              />
            ) : (
              <div className="flex h-16 w-16 items-center justify-center rounded-full border border-[var(--color-divider)]">
                <Star size={22} className="text-[var(--color-text-secondary)]" />
              </div>
            )}
            <p className="mt-2 text-center text-3xl font-semibold text-[var(--color-text-primary)]">{displayName}</p>
            <p className="text-center text-lg text-[var(--color-text-secondary)]">{archetypeName}</p>
          </div>
        )}

        <section className="mt-5 rounded-xl border border-dashed border-[var(--color-divider)] bg-[var(--color-bg-primary)]/80 p-4">
          <h2 className="text-lg font-semibold text-[var(--color-text-primary)]">My Dogs</h2>
          <ul className="mt-3 space-y-2">
            {myDogs.map((packDog) => {
              const portraitSrc = heroPortraitUrl(packDog.archetype_icon, packDog.portrait)
              return (
                <li key={packDog.id}>
                  <button
                    type="button"
                    onClick={() => handleOpenDogDetails(packDog.id)}
                    aria-label={`Open ${packDog.name} details`}
                    className="flex w-full items-center gap-3 rounded-lg border border-[var(--color-divider)] bg-[var(--color-surface-card)] px-3 py-2 text-left hover:bg-[var(--color-bg-primary)]"
                  >
                    {portraitSrc ? (
                      <img
                        src={portraitSrc}
                        alt=""
                        className="h-12 w-12 rounded-full object-cover"
                      />
                    ) : (
                      <div className="flex h-12 w-12 items-center justify-center rounded-full border border-[var(--color-divider)]">
                        <PawPrint size={18} />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium text-[var(--color-text-primary)]">{packDog.name}</p>
                      <p className="truncate text-sm text-[var(--color-text-secondary)]">
                        {packDog.archetype_label ?? "Personality"}
                      </p>
                    </div>
                    <ChevronRight size={16} className="text-[var(--color-text-secondary)]" />
                  </button>
                </li>
              )
            })}
          </ul>
          <Link
            to={isPlusUser ? "/pack/add" : "/plus"}
            className="mt-3 flex items-center justify-between py-2 text-sm font-medium text-[var(--color-brand-primary)]"
          >
            <span>Add another dog</span>
            <ChevronRight size={16} />
          </Link>
        </section>

        <section className="mt-5 rounded-xl border border-dashed border-[var(--color-divider)] bg-[var(--color-bg-primary)]/80 p-4">
          <h2 className="text-lg font-semibold text-[var(--color-text-primary)]">My Account</h2>
          <div className="mt-3 divide-y divide-[var(--color-divider)] border-t border-[var(--color-divider)]">
            {isPlusUser ? (
              <>
                <Link
                  to="/pack/manage"
                  className="flex items-center justify-between py-3 text-sm text-[var(--color-text-primary)]"
                >
                  <span>Manage Dogs</span>
                  <ChevronRight size={16} />
                </Link>
                <Link
                  to="/subscription"
                  className="flex items-center justify-between py-3 text-sm text-[var(--color-text-primary)]"
                >
                  <span>Subscription</span>
                  <ChevronRight size={16} />
                </Link>
              </>
            ) : (
              <Link
                to="/plus"
                className="flex items-center justify-between py-3 text-sm font-medium text-[var(--color-brand-primary)]"
              >
                <span>Build Your Pack with Plus</span>
                <ChevronRight size={16} />
              </Link>
            )}
            <Link
              to="/account"
              className="flex items-center justify-between py-3 text-sm text-[var(--color-text-primary)]"
            >
              <span>Account Details</span>
              <ChevronRight size={16} />
            </Link>
            <Link
              to="/notifications"
              className="flex items-center justify-between py-3 text-sm text-[var(--color-text-primary)]"
            >
              <span>Notifications</span>
              <ChevronRight size={16} />
            </Link>
            <Link
              to="/privacy"
              className="flex items-center justify-between py-3 text-sm text-[var(--color-text-primary)]"
            >
              <span>Privacy</span>
              <ChevronRight size={16} />
            </Link>
          </div>
        </section>

        {dog ? (
          <section className="mt-4 rounded-xl border border-dashed border-[var(--color-divider)] bg-[var(--color-bg-primary)]/80 p-4">
            <h2 className="text-lg font-semibold text-[var(--color-text-primary)]">
              About {dogName.trim() || dog.name}
            </h2>
            <label className="mt-3 block text-center text-sm text-[var(--color-text-secondary)]" htmlFor="profile-dog-name">
              Name
            </label>
            <input
              id="profile-dog-name"
              value={dogName}
              onChange={(e) => setDogName(e.target.value)}
              className="mt-1 w-full rounded-lg border border-[var(--color-divider)] bg-[var(--color-surface-card)] px-3 py-2 text-center text-[var(--color-text-primary)]"
            />
            <p className="mt-3 text-center text-sm text-[var(--color-text-secondary)]">Sex</p>
            <div className="mt-2 flex justify-center gap-3">
              <button
                type="button"
                onClick={() => setDogSex("male")}
                aria-pressed={dogSex === "male"}
                className={`min-w-28 rounded-lg border px-4 py-2 text-sm font-medium transition ${
                  dogSex === "male"
                    ? "border-[var(--color-text-primary)] bg-[var(--color-text-primary)] text-[var(--color-surface-card)]"
                    : "border-[var(--color-divider)] bg-[var(--color-bg-primary)] text-[var(--color-text-primary)]"
                }`}
              >
                Male
              </button>
              <button
                type="button"
                onClick={() => setDogSex("female")}
                aria-pressed={dogSex === "female"}
                className={`min-w-28 rounded-lg border px-4 py-2 text-sm font-medium transition ${
                  dogSex === "female"
                    ? "border-[var(--color-text-primary)] bg-[var(--color-text-primary)] text-[var(--color-surface-card)]"
                    : "border-[var(--color-divider)] bg-[var(--color-bg-primary)] text-[var(--color-text-primary)]"
                }`}
              >
                Female
              </button>
            </div>
            <div className="mt-3">
              <SearchableBreedSelector
                value={dogBreed}
                mixDetail={dogMixDetail}
                onChange={(breed, mixDetail) => {
                  setDogBreed(breed)
                  setDogMixDetail(mixDetail ?? "")
                }}
                onValidityChange={setBreedValid}
                placeholder="Search or select breed (optional)"
              />
            </div>
            {saveError && (
              <p className="mt-2 text-center text-sm text-red-600" role="alert">
                {saveError}
              </p>
            )}
            <button
              type="button"
              disabled={!canSaveDog || saving}
              onClick={handleSaveDogProfile}
              className="mt-3 w-full rounded-lg bg-[var(--color-text-primary)] px-4 py-2 text-sm font-medium text-[var(--color-surface-card)] disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save changes"}
            </button>
          </section>
        ) : (
          <p className="mt-4 text-center text-sm text-[var(--color-text-secondary)]">
            No dog profile yet. Complete an assessment to add your dog.
          </p>
        )}

        {profile && dog && (
          <HeroPortraitCard profile={profile} onProfileUpdated={setProfile} />
        )}

        <ProfileFeedbackSection />

        <div className="mt-4 flex flex-col items-center gap-3">
          <Link
            to="/assessment?retake=1"
            className="inline-flex w-full max-w-xs justify-center rounded-full border border-[var(--color-divider)] bg-[var(--color-bg-primary)] px-6 py-3 text-sm font-medium text-[var(--color-text-primary)] shadow-sm"
          >
            Retake assessment
          </Link>
          <Link to="/deeper-insights" className="text-sm text-[var(--color-text-primary)] underline">
            Explore what&apos;s coming →
          </Link>
        </div>

        <button
          type="button"
          onClick={handleLogout}
          className="mt-4 w-full text-center text-sm text-[var(--color-text-secondary)] underline"
        >
          Log out
        </button>

      </LoggedInScreenCard>
    </PageShell>
  )
}
